import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDeletePortfolioCost } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { AdvisorOverrideBadge } from "./advisor-override-badge";
import { PortfolioCostDialog } from "./portfolio-cost-dialog";
import { T } from "./portfolio-costs-table.i18n";

type PortfolioCostsTableProps = {
  portfolioCollectionId: string;
  portfolioCosts: any[];
};

export function PortfolioCostsTable({
  portfolioCollectionId,
  portfolioCosts,
}: PortfolioCostsTableProps) {
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);

  const deletePortfolioCost = useDeletePortfolioCost();
  const isPending = deletePortfolioCost.isPending;

  const COST_TYPE_LABELS: Record<string, string> = {
    amount: t.costTypeAmount,
    pct: t.costTypePercentage,
  };

  const CATEGORY_LABELS: Record<string, string> = {
    RECURRING: t.categoryRecurring,
    DEPOSIT: t.categoryDeposit,
    WITHDRAWAL: t.categoryWithdrawal,
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPortfolioCost, setEditingPortfolioCost] = useState<any | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [portfolioCostToDelete, setPortfolioCostToDelete] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  const handleEdit = (portfolioCost: any) => {
    setEditingPortfolioCost(portfolioCost);
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (!portfolioCostToDelete) return;

    deletePortfolioCost.mutate(
      { collectionId: portfolioCollectionId, pcId: portfolioCostToDelete },
      {
        onSuccess: () => toast.success(t.portfolioCostDeleted),
        onError: (err) => showAlert(t.error, err.message || t.failedToDelete, "destructive"),
      },
    );
    setPortfolioCostToDelete(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingPortfolioCost(null);
  };

  const formatValue = (portfolioCost: any): string => {
    const isPct = portfolioCost.costType === "pct";
    const brackets = portfolioCost.brackets;

    if (brackets.length === 0) return t.notSet;

    // Simple cost (not using brackets) - just show the value
    if (!portfolioCost.useBrackets) {
      const b = brackets[0];
      return isPct ? `${b.value}%` : formatAmount(b.value);
    }

    // Tiered cost - show bracket summary
    if (brackets.length === 1) {
      const b = brackets[0];
      return isPct ? `${b.value}%` : formatAmount(b.value);
    }

    // Show first two brackets with summary
    const formatted = brackets.slice(0, 2).map((b: any) => {
      const startFormatted = formatAmount(b.startValue, true);
      const valueFormatted = isPct ? `${b.value}%` : formatAmount(b.value);
      return `${startFormatted}→${valueFormatted}`;
    });

    if (brackets.length > 2) {
      return `${formatted.join(", ")} +${brackets.length - 2} ${t.more}`;
    }

    return formatted.join(", ");
  };

  const formatAmount = (value: number, compact = false): string => {
    if (compact && value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (compact && value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return formatNumber(value);
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-muted-foreground">{t.description}</p>
          <Button onClick={() => setDialogOpen(true)}>{t.addPortfolioCost}</Button>
        </div>

        {portfolioCosts.length === 0 ? (
          <EmptyState>{t.noPortfolioCosts}</EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.category}</TableHead>
                <TableHead>{t.type}</TableHead>
                <TableHead>{t.value}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {portfolioCosts.map((portfolioCost) => (
                <TableRow key={portfolioCost.id}>
                  <TableCell className="font-medium">
                    {localize(portfolioCost.nameI18n)}
                    <AdvisorOverrideBadge mode={portfolioCost.advisorOverride} />
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {CATEGORY_LABELS[portfolioCost.category] || portfolioCost.category}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {COST_TYPE_LABELS[portfolioCost.costType] || portfolioCost.costType}
                      </Badge>
                      {portfolioCost.useBrackets && (
                        <Badge variant="outline" className="text-xs">
                          {t.tiered}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatValue(portfolioCost)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={() => handleEdit(portfolioCost)}
                        disabled={isPending}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        onClick={() => {
                          setPortfolioCostToDelete(portfolioCost.id);
                          setConfirmDeleteOpen(true);
                        }}
                        disabled={isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <PortfolioCostDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        portfolioCollectionId={portfolioCollectionId}
        portfolioCost={editingPortfolioCost}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deletePortfolioCost}
        description={t.deleteConfirmation}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
